import { themes, radius, family, type } from '@going-once/tokens';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { runtimeConfig } from './config';

/**
 * Stripe.js, loaded on demand, and the Payment Element's theming.
 *
 * **The script is loaded from `js.stripe.com` and cannot be bundled.** That is the point
 * of it rather than a limitation: the card fields live in an iframe served by Stripe, so
 * the number is typed into Stripe's page and never enters ours. Self-hosting it would put
 * our origin back in the path of a PAN and pull the whole app into PCI scope — which is
 * the outcome `orders.paymentmethod.setup` is shaped to avoid.
 *
 * **Nothing reaches js.stripe.com until somebody opens the card sheet.** The `loadStripe`
 * loader is a few kB and the bundler hoists it into the main chunk; what is deferred is
 * the part that matters — the ~200 kB script itself, and the request for it. Stripe.js is
 * a third-party script that sees every page it is loaded on, so a guest browsing the feed
 * should not be introduced to it. That is a privacy property, not a byte count, and it
 * holds because `loadStripe` is only called inside `getStripe` below.
 *
 * The type-only import above is erased at compile time and pulls nothing in.
 */

let loader: Promise<Stripe | null> | undefined;

/**
 * The publishable key, or `undefined` when this environment has none.
 *
 * `ApiStack` writes `pk_unset` and a person sets the real value, so an unconfigured
 * environment is legitimate — and the screen saying so beats Stripe.js loading against a
 * bad key, which fails inside an iframe at the moment someone presses Save.
 */
export function stripeConfigured(): boolean {
  return Boolean(runtimeConfig().stripePublishableKey);
}

export async function getStripe(): Promise<Stripe> {
  const key = runtimeConfig().stripePublishableKey;
  if (!key) throw new Error('This environment has no Stripe publishable key.');
  if (!loader) {
    loader = import('@stripe/stripe-js').then(({ loadStripe }) => loadStripe(key));
  }
  const stripe = await loader;
  if (!stripe) {
    // `loadStripe` resolves null when the script could not load at all — an ad blocker,
    // or a network that refuses js.stripe.com. Worth its own message: it looks exactly
    // like a broken page otherwise, and the person can do something about this one.
    throw new Error('Stripe could not be loaded. A tracker blocker or network policy may be blocking js.stripe.com.');
  }
  return stripe;
}

/**
 * Our tokens, in the shape Stripe's `appearance` API takes.
 *
 * **This is the one place real color values leave the token contract**, and it is
 * unavoidable: the Element renders in a cross-origin iframe, so it cannot read our CSS
 * variables and has to be handed values. Reading them from `themes` rather than writing
 * hexes keeps the single source of truth intact — when design supplies the real palette,
 * this follows without an edit (D24).
 */
export function elementsAppearance(theme: 'dark' | 'light' = 'dark') {
  const palette = themes[theme];
  return {
    theme: (theme === 'dark' ? 'night' : 'stripe') as 'night' | 'stripe',
    variables: {
      colorPrimary: palette['accent.default'],
      colorBackground: palette['bg.sunken'],
      colorText: palette['fg.default'],
      colorTextSecondary: palette['fg.muted'],
      colorDanger: palette.danger,
      fontFamily: family.base,
      borderRadius: `${radius.md}px`,
      fontSizeBase: `${type.body.size}px`,
    },
  };
}

export type { Stripe, StripeElements };
